import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, IconButton, Box, Stepper, Step, StepLabel, Tabs, Tab} from '@mui/material'
import {ArrowBack, CheckCircle} from '@mui/icons-material'
import {scheduleApi, clientApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useAuth} from '../../contexts/AuthContext'
import {useNavigate, Link, useParams} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {parseError, ignoreTimezone} from '../../utils/helpers'
import {generateRRule, parseRRule, RECURRING_TYPES} from '../../utils/rruleGenerator'
import {useMobile} from '../../hooks'
import {SkeletonContainer, FieldSkeleton} from '../../components'
import {VisitDetails, VisitRequirements, VisitSchedule, OverlapsWarningDialog} from './components'
import {format, isAfter, isBefore, isValid, startOfDay} from 'date-fns'

const toDay = value => {
    if (!value) return null
    const day = startOfDay(new Date(value))
    return isValid(day) ? day : null
}

// Keeps a date inside [min, max]; either end may be open. Days compare whole.
const withinRange = (schema, label, min, max) =>
    schema
        .test(
            'min-date',
            `${label} must be on or after ${min ? format(min, 'MM/dd/yyyy') : ''}`,
            value => !value || !min || !isBefore(startOfDay(value), min),
        )
        .test(
            'max-date',
            `${label} must be on or before ${max ? format(max, 'MM/dd/yyyy') : ''}`,
            value => !value || !max || !isAfter(startOfDay(value), max),
        )

const blankToNull = (value, originalValue) => (originalValue === '' ? null : value)

// "HH:mm" or "HH:mm:ss" as minutes since midnight; null when it isn't a time.
const toMinutes = time => {
    const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(time ?? '')
    return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

// What saving did to the schedule's visits, in the order they are reported. Updated visits are upcoming ones unless
// the edit was applied to past visits too.
const visitChangeKinds = visitChanges => [
    ['updated', Number(visitChanges?.past_updated) > 0 ? 'visit' : 'upcoming visit'],
    ['created', 'visit'],
    ['removed', 'visit'],
]

// "3 upcoming visits updated, 1 created, 2 removed": only the non-zero counts, and only the first carries the noun.
// '' when nothing changed or the response has no visit_changes.
const describeVisitChanges = visitChanges =>
    visitChangeKinds(visitChanges)
        .map(([verb, noun]) => ({verb, noun, n: Number(visitChanges?.[verb]) || 0}))
        .filter(({n}) => n > 0)
        .map(({verb, noun, n}, index) => (index === 0 ? `${n} ${noun}${n === 1 ? '' : 's'} ${verb}` : `${n} ${verb}`))
        .join(', ')

const VISIT_CHANGES_TOAST_MS = 7000

// Stands in for the steps while the schedule being edited loads; they mount once it has.
const StepsSkeleton = () => (
    <SkeletonContainer>
        {[0, 1, 2, 3].map(index => (
            <FieldSkeleton key={index} sx={{mb: 2}} />
        ))}
    </SkeletonContainer>
)

// Whether the chosen recurrence repeats and is ended "on" a date or "after" a count.
// A one-time schedule (or none chosen yet, which also becomes one occurrence) has no end.
const endedBy = (type, ends, kind) => RECURRING_TYPES.includes(type) && ends === kind

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {organization} = useAuth()
    const [activeStep, setActiveStep] = useState(0)
    const [nextLabel, setNextLabel] = useState('Next')
    const navigate = useNavigate()
    const {mobile} = useMobile()
    const [visitDetailsData, setVisitDetailsData] = useState(null)
    const [visitRequirementsData, setVisitRequirementsData] = useState(null)
    const [visitScheduleData, setVisitScheduleData] = useState(null)
    const [openOverlapsWarningDialog, setOpenOverlapsWarningDialog] = useState(false)
    const [overlapMessage, setOverlapMessage] = useState('')
    const {scheduleId} = useParams()
    // When editing, the steps mount once the schedule has been read into the forms,
    // so they never start from a blank form while the schedule is still loading.
    const [scheduleLoaded, setScheduleLoaded] = useState(!scheduleId)
    const [skeleton, setSkeleton] = useState(!!scheduleId)
    // True while VisitDetails loads the chosen client's caregivers, agreement and address.
    const [lookupsSkeleton, setLookupsSkeleton] = useState(false)
    const steps = ['Visit Details', 'Visit Requirements', 'Visit Schedule']

    const visitDetailsSchema = yup.object().shape({
        client_id: yup.string().required('Client is required'),
        caregiver_id: yup.string().required('Caregiver is required'),
        service_agreement_id: yup.string(),
        address_id: yup.string(),
        address: yup.object(),
        notes: yup.string(),
        service_items: yup.array().of(yup.object()),
    })
    const visitDetailsForm = useForm({
        resolver: yupResolver(visitDetailsSchema),
        defaultValues: visitDetailsSchema.getDefault(),
    })

    const visitRequirementsSchema = yup.object().shape({
        service_agreement_item_id: yup.string().required('Service care is required'),
        start_date: yup
            .date()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value)),
        end_date: yup
            .date()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value)),
        total_cost: yup
            .number()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value)),
    })
    const visitRequirementsForm = useForm({
        resolver: yupResolver(visitRequirementsSchema),
        defaultValues: visitRequirementsSchema.getDefault(),
    })

    // The dates the chosen service care covers; the visit dates must stay inside them.
    const itemStart = toDay(visitRequirementsData?.start_date)
    const itemEnd = toDay(visitRequirementsData?.end_date)
    const visitScheduleSchema = yup.object().shape({
        rrule: yup.string(),
        timezone: yup.string(),
        description: yup.string(),
        occurrence_type: yup.string(),
        occurrence_number: yup
            .number()
            .nullable()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .default(1),
        occurrence_period: yup.string().default('week'),
        repeat_on_monday: yup.boolean().default(false),
        repeat_on_tuesday: yup.boolean().default(false),
        repeat_on_wednesday: yup.boolean().default(false),
        repeat_on_thursday: yup.boolean().default(false),
        repeat_on_friday: yup.boolean().default(false),
        repeat_on_saturday: yup.boolean().default(false),
        repeat_on_sunday: yup.boolean().default(false),
        start_time: yup.string().required('Start time is required'),
        // Visits can't run past midnight. Compared as minutes (a saved end time may carry seconds), and only
        // once both times are filled in, so a blank one is reported as required rather than as out of order.
        end_time: yup
            .string()
            .required('End time is required')
            .test('end-after-start', 'End time must be after start time', function (value) {
                const start = toMinutes(this.parent.start_time)
                const end = toMinutes(value)
                return start === null || end === null || end > start
            }),
        occurrence_start_date: withinRange(
            yup.date().typeError('Enter a valid start date').nullable().transform(blankToNull).required('Start date is required'),
            'Start date',
            itemStart,
            itemEnd,
        ),
        // Null for one-time schedules (parseRRule has no end to report for them).
        occurrence_ends: yup
            .string()
            .nullable()
            .default('on')
            .when('occurrence_type', ([type], schema) => (RECURRING_TYPES.includes(type) ? schema.required('Choose how the schedule ends') : schema)),
        occurrence_end_date: yup
            .mixed()
            .when(['occurrence_type', 'occurrence_ends'], ([type, ends]) =>
                endedBy(type, ends, 'on')
                    ? withinRange(
                          yup.date().typeError('Enter a valid end date').nullable().transform(blankToNull).required('End date is required'),
                          'End date',
                          itemStart,
                          itemEnd,
                      )
                    : yup.mixed().nullable(),
            ),
        occurrence_total: yup
            .string()
            .nullable()
            .when(['occurrence_type', 'occurrence_ends'], ([type, ends], schema) =>
                endedBy(type, ends, 'after')
                    ? schema
                          .required('Number of occurrences is required')
                          .matches(/^[1-9]\d*$/, {message: 'Enter a whole number greater than 0', excludeEmptyString: true})
                    : schema,
            ),
        // Which visits an edit changes: upcoming ones, or also past ones nobody worked, which needs a reason.
        apply_to: yup.string().default('upcoming'),
        apply_reason: yup
            .string()
            .default('')
            .when('apply_to', ([applyTo], schema) =>
                applyTo === 'all'
                    ? schema.trim().required('Enter a reason to change past visits').max(500, 'Keep the reason under 500 characters')
                    : schema,
            ),
    })
    const visitScheduleForm = useForm({
        resolver: yupResolver(visitScheduleSchema),
        defaultValues: visitScheduleSchema.getDefault(),
    })

    useEffect(() => {
        if (branchId && scheduleId) {
            setSkeleton(true)
            scheduleApi
                .getSchedule(branchId, scheduleId)
                .then(({data}) => {
                    setSkeleton(false)
                    visitDetailsForm.setValue('client_id', data?.client_id)
                    visitDetailsForm.setValue('caregiver_id', data?.caregiver_id)
                    visitDetailsForm.setValue('service_agreement_id', data?.service_agreement_id)
                    visitDetailsForm.setValue('address_id', data?.address_id)
                    visitDetailsForm.setValue('notes', data?.notes)
                    visitRequirementsForm.setValue('service_agreement_item_id', data?.service_agreement_item_id)
                    visitScheduleForm.setValue('start_date', data?.start_date ? new Date(ignoreTimezone(data?.start_date)) : '')
                    visitScheduleForm.setValue('end_date', data?.end_date ? new Date(ignoreTimezone(data?.end_date)) : '')
                    visitScheduleForm.setValue('start_time', data?.start_time)
                    visitScheduleForm.setValue('end_time', data?.end_time)
                    visitScheduleForm.setValue('total_cost', data?.total_cost)
                    visitScheduleForm.setValue('rrule', data?.rrule)
                    visitScheduleForm.setValue('timezone', data?.timezone)
                    // The rule is read back in the zone it was built in: the schedule's own.
                    const visitSchedule = parseRRule(data?.rrule, data?.end_time, data?.timezone || organization?.timezone)
                    visitScheduleForm.setValue('occurrence_type', visitSchedule?.occurrence_type)
                    visitScheduleForm.setValue('occurrence_number', visitSchedule?.occurrence_number)
                    visitScheduleForm.setValue('occurrence_period', visitSchedule?.occurrence_period)
                    visitScheduleForm.setValue('repeat_on_monday', visitSchedule?.repeat_on_monday)
                    visitScheduleForm.setValue('repeat_on_tuesday', visitSchedule?.repeat_on_tuesday)
                    visitScheduleForm.setValue('repeat_on_wednesday', visitSchedule?.repeat_on_wednesday)
                    visitScheduleForm.setValue('repeat_on_thursday', visitSchedule?.repeat_on_thursday)
                    visitScheduleForm.setValue('repeat_on_friday', visitSchedule?.repeat_on_friday)
                    visitScheduleForm.setValue('repeat_on_saturday', visitSchedule?.repeat_on_saturday)
                    visitScheduleForm.setValue('repeat_on_sunday', visitSchedule?.repeat_on_sunday)
                    // The saved start time is the wall-clock time the visits are built from, as "HH:mm:ss". The rule's
                    // DTSTART only stands in for it when there is none.
                    visitScheduleForm.setValue('start_time', data?.start_time?.slice(0, 5) || visitSchedule?.start_time)
                    visitScheduleForm.setValue('end_time', visitSchedule?.end_time)
                    visitScheduleForm.setValue(
                        'occurrence_start_date',
                        visitSchedule?.occurrence_start_date ? new Date(ignoreTimezone(visitSchedule?.occurrence_start_date)) : '',
                    )
                    visitScheduleForm.setValue('occurrence_ends', visitSchedule?.occurrence_ends ?? 'on')
                    visitScheduleForm.setValue(
                        'occurrence_end_date',
                        visitSchedule?.occurrence_end_date ? new Date(ignoreTimezone(visitSchedule?.occurrence_end_date)) : '',
                    )
                    visitScheduleForm.setValue('occurrence_total', visitSchedule?.occurrence_total)
                    setScheduleLoaded(true)
                })
                .catch(error => {
                    setSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }, [branchId])

    useEffect(() => {
        if (activeStep == 2) {
            setNextLabel('Submit')
        } else {
            setNextLabel('Next')
        }
    }, [activeStep])

    useEffect(() => {
        if (visitScheduleData) {
            submit()
        }
    }, [visitScheduleData])

    const previous = () => {
        setActiveStep(prev => --prev)
    }

    // The fields show their own messages; this makes sure a rejected click is never silent.
    const onInvalid = () => {
        setMessageBox({status: 'error', title: 'Check the form', description: 'Please fix the highlighted fields and try again.'})
    }

    const next = () => {
        if (activeStep == 0) {
            visitDetailsForm.handleSubmit(data => {
                setVisitDetailsData(data)
                setActiveStep(prev => ++prev)
            }, onInvalid)()
        } else if (activeStep == 1) {
            visitRequirementsForm.handleSubmit(data => {
                setVisitRequirementsData(data)
                setActiveStep(prev => ++prev)
            }, onInvalid)()
        } else if (activeStep == 2) {
            visitScheduleForm.handleSubmit(data => {
                setVisitScheduleData(data)
            }, onInvalid)()
        }
    }

    const submit = async () => {
        try {
            setLoading(true)
            // The zone the schedule's wall-clock times are in: its own when editing, else the agency's. '' when
            // neither is known; the request then omits it and the backend applies the agency's.
            const timezone = visitScheduleData.timezone || organization?.timezone || ''
            const {rrule} = generateRRule(visitScheduleData, timezone)
            // Only an "ends on" rule carries an end date; for a count or a one-time schedule the backend derives it.
            const {occurrence_type, occurrence_ends, occurrence_end_date} = visitScheduleData
            const endDate = endedBy(occurrence_type, occurrence_ends, 'on') && occurrence_end_date
            const body = {
                client_id: visitDetailsData?.client_id,
                caregiver_id: visitDetailsData?.caregiver_id,
                service_agreement_id: visitDetailsData?.service_agreement_id,
                address_id: visitDetailsData?.address_id,
                notes: visitDetailsData?.notes,
                service_agreement_item_id: visitRequirementsData?.service_agreement_item_id,
                start_date: visitScheduleData?.occurrence_start_date ? format(new Date(visitScheduleData?.occurrence_start_date), 'yyyy-MM-dd') : '',
                end_date: endDate ? format(new Date(endDate), 'yyyy-MM-dd') : '',
                start_time: visitScheduleData?.start_time,
                end_time: visitScheduleData?.end_time,
                total_cost: visitRequirementsData?.total_cost,
                rrule,
                ...(timezone && {timezone}),
                // Upcoming visits are the backend's default, so only the opt-in to past visits is sent.
                ...(scheduleId && visitScheduleData.apply_to === 'all' && {apply_to: 'all', apply_reason: visitScheduleData.apply_reason.trim()}),
            }

            // The endpoints return the schedule itself plus visit_changes, not wrapped in {data: ...}.
            const {data: saved} = !scheduleId
                ? await scheduleApi.createSchedule(branchId, body)
                : await scheduleApi.updateSchedule(branchId, scheduleId, body)

            await clientApi.updateClientAddress(visitDetailsData?.client_id, visitDetailsData?.address_id, visitDetailsData?.address)

            setLoading(false)
            const visitChanges = describeVisitChanges(saved?.visit_changes)
            setMessageBox({
                status: 'success',
                title: !scheduleId ? 'Schedule Added Successfully' : 'Schedule Updated Successfully',
                description: visitChanges || (!scheduleId ? 'The new schedule has been added.' : 'The schedule has been updated'),
                // The summary is longer than the usual toast, so it stays up longer.
                ...(visitChanges && {duration: VISIT_CHANGES_TOAST_MS}),
            })
            navigate('/scheduling')
        } catch (error) {
            setLoading(false)
            const {title, description} = parseError(error)
            if (title === 'CONFLICT') {
                // A caregiver double-booking names who is booked and when; show it with the warning.
                setOverlapMessage(description || '')
                setOpenOverlapsWarningDialog(true)
            } else if (title === 'INTERNAL_ERROR' && description?.includes('duplicate key')) {
                setOverlapMessage('')
                setOpenOverlapsWarningDialog(true)
            } else if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <Link to="/scheduling">
                            <IconButton>
                                <ArrowBack />
                            </IconButton>
                        </Link>
                        <Typography variant="h6" color="text.primary">
                            {scheduleId ? 'Update Schedule' : 'Add New Schedule'}
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" onClick={() => navigate('/scheduling')} style={{flex: 1}}>
                            Cancel
                        </Button>
                        {activeStep != 0 && (
                            <Button variant="outlined" onClick={previous} style={{flex: 1}}>
                                Previous
                            </Button>
                        )}
                        <Button variant="contained" onClick={next} disabled={skeleton || lookupsSkeleton} style={{flex: 1}}>
                            {nextLabel}
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        <Link className="link" to="/scheduling">
                            Schedule Management
                        </Link>
                        {' / '} {scheduleId ? 'Update Schedule' : 'Add New Schedule'}
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <Link className="back-button" to="/scheduling">
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </Link>
                            <Typography variant="h5" color="text.primary">
                                {scheduleId ? 'Update Schedule' : 'Add New Schedule'}
                            </Typography>
                        </div>
                        <div className="actions">
                            <Button variant="outlined" onClick={() => navigate('/scheduling')}>
                                Cancel
                            </Button>
                            {activeStep != 0 && (
                                <Button variant="outlined" onClick={previous}>
                                    Previous
                                </Button>
                            )}
                            <Button variant="contained" onClick={next} disabled={skeleton || lookupsSkeleton}>
                                {nextLabel}
                            </Button>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            {mobile ? (
                <>
                    <Tabs value={activeStep} onChange={(e, newValue) => setActiveStep(newValue)} variant="scrollable">
                        <Tab label="Visit Details" disabled />
                        <Tab label="Visit Requirements" disabled />
                        <Tab label="Visit Schedule" disabled />
                    </Tabs>
                    <br />
                    <br />
                    {skeleton && <StepsSkeleton />}
                    {scheduleLoaded && (
                        <>
                            <div style={{display: activeStep != 0 ? 'none' : 'block'}}>
                                <VisitDetails form={visitDetailsForm} loading={skeleton} setLookupsSkeleton={setLookupsSkeleton} />
                            </div>
                            <div style={{display: activeStep != 1 ? 'none' : 'block'}}>
                                <VisitRequirements form={visitRequirementsForm} visitDetailsData={visitDetailsData} />
                            </div>
                            <div style={{display: activeStep != 2 ? 'none' : 'block'}}>
                                <VisitSchedule form={visitScheduleForm} visitRequirementsData={visitRequirementsData} editing={!!scheduleId} />
                            </div>
                        </>
                    )}
                </>
            ) : (
                <div className="container">
                    <div className="step">
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={label} completed={index < activeStep}>
                                    <StepLabel
                                        slots={{
                                            stepIcon: ({active, completed, icon}) =>
                                                completed ? (
                                                    <CheckCircle color="primary" style={{fontSize: 28}} />
                                                ) : (
                                                    <Box
                                                        className="step-icon"
                                                        sx={theme => ({
                                                            backgroundColor: active ? theme.palette.primary.main : theme.palette.text.disabled,
                                                        })}>
                                                        {icon}
                                                    </Box>
                                                ),
                                        }}>
                                        <Typography variant="subtitle2">{label}</Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>
                    <div className="content">
                        {skeleton && <StepsSkeleton />}
                        {scheduleLoaded && (
                            <>
                                {activeStep == 0 && (
                                    <VisitDetails form={visitDetailsForm} loading={skeleton} setLookupsSkeleton={setLookupsSkeleton} />
                                )}
                                {activeStep == 1 && <VisitRequirements form={visitRequirementsForm} visitDetailsData={visitDetailsData} />}
                                {activeStep == 2 && (
                                    <VisitSchedule form={visitScheduleForm} visitRequirementsData={visitRequirementsData} editing={!!scheduleId} />
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            <OverlapsWarningDialog open={openOverlapsWarningDialog} setOpen={setOpenOverlapsWarningDialog} message={overlapMessage} />
        </>
    )
}
